/* Scale the actual responsive size, including global typography and local ratios. */
(() => {
  const customHeadings = new Map();
  const customPending = new Set();
  const customAddedRoots = new Set();
  const customRemovedRoots = new Set();
  const customObservedStyles = new WeakSet();
  let customFrame;
  let customTimer;
  let customBatchStarted = 0;
  const customProperties = ['font-size', 'line-height'];
  const customHeadingSelector = '[data-custom-heading-scale]';
  const customStylesheetSelector = 'style, link[rel~="stylesheet"]';
  const customDiscoverySelector = `${customHeadingSelector}, style, link`;

  function customRestore(customHeading, customOriginal) {
    customProperties.forEach((customProperty, customIndex) => {
      const [customValue, customPriority] = customOriginal[customIndex];
      if (customValue) customHeading.style.setProperty(customProperty, customValue, customPriority);
      else customHeading.style.removeProperty(customProperty);
    });
  }

  function customUpdate() {
    customFlushRoots();
    customFrame = null;
    const customActive = [];
    customPending.forEach(customHeading => {
      const customEntry = customHeadings.get(customHeading);
      if (!customEntry) return;
      const customOriginal = customEntry.customOriginal;
      if (!customHeading.isConnected || !customHeading.matches(customHeadingSelector)) {
        customRestore(customHeading, customOriginal);
        customHeading.removeAttribute('data-custom-heading-measuring');
        customHeading.removeAttribute('data-custom-heading-alignment-changed');
        customHeadings.delete(customHeading);
        return;
      }
      if (!customEntry.customMeasurement) {
        customRestore(customHeading, customOriginal);
        customHeading.setAttribute('data-custom-heading-measuring', '');
      }
      const customScale = Number(customHeading.dataset.customHeadingScale) / 100;
      customActive.push({ customHeading, customScale, customEntry });
    });
    customPending.clear();
    // Batch reads after restoring all baselines; no cumulative scaling on resize.
    const customMeasurements = customActive.map(({ customHeading, customScale, customEntry }) => {
      if (!customEntry.customMeasurement) {
        const customStyle = getComputedStyle(customHeading);
        customEntry.customMeasurement = {
          customSize: parseFloat(customStyle.fontSize), customLine: parseFloat(customStyle.lineHeight),
          customNativeAlignment: customStyle.textAlign === 'start' ? (customStyle.direction === 'rtl' ? 'right' : 'left') : customStyle.textAlign === 'end' ? (customStyle.direction === 'rtl' ? 'left' : 'right') : customStyle.textAlign
        };
      }
      const customDeviceAlignment = window.innerWidth < 700 ? customHeading.dataset.customHeadingAlignMobile : window.innerWidth < 1000 ? customHeading.dataset.customHeadingAlignTablet : customHeading.dataset.customHeadingAlign;
      return { customHeading, customScale, customEntry, ...customEntry.customMeasurement, customAlignmentChanged: customDeviceAlignment !== customEntry.customMeasurement.customNativeAlignment };
    });
    customMeasurements.forEach(({ customHeading, customScale, customEntry, customSize, customLine, customAlignmentChanged }) => {
      customHeading.removeAttribute('data-custom-heading-measuring');
      customHeading.toggleAttribute('data-custom-heading-alignment-changed', customAlignmentChanged);
      if (Number.isFinite(customScale) && customScale > 0 && customScale !== 1) {
        customHeading.style.setProperty('font-size', `${customSize * customScale}px`, 'important');
        if (Number.isFinite(customLine)) customHeading.style.setProperty('line-height', `${customLine * customScale}px`, 'important');
      } else {
        customRestore(customHeading, customEntry.customOriginal);
      }
    });
  }

  function customSchedule() {
    if (!customFrame) customFrame = requestAnimationFrame(customUpdate);
  }

  function customInvalidate(customHeading) {
    const customEntry = customHeadings.get(customHeading);
    if (!customEntry) return;
    customEntry.customMeasurement = null;
    customPending.add(customHeading);
  }

  function customInvalidateAll() {
    customHeadings.forEach((customEntry, customHeading) => customInvalidate(customHeading));
    customSchedule();
  }

  // Styles and fonts arrive in bursts. Bound the delay so continuous app updates
  // cannot starve a real typography change; resize/editor updates stay immediate.
  function customScheduleStyles() {
    const customNow = performance.now();
    if (!customTimer) customBatchStarted = customNow;
    clearTimeout(customTimer);
    customTimer = setTimeout(() => {
      customTimer = null;
      customInvalidateAll();
    }, Math.min(80, Math.max(0, 200 - (customNow - customBatchStarted))));
  }

  function customRegister(customRoot) {
    if (!(customRoot instanceof Element || customRoot instanceof Document)) return;
    // Discover headings and styles in one pass. App fragments often contain
    // nested mutation records, so callers batch those roots before scanning.
    const customNodes = customRoot.childElementCount ? [...customRoot.querySelectorAll(customDiscoverySelector)] : [];
    if (customRoot.matches?.(customDiscoverySelector)) customNodes.push(customRoot);
    let customStylesChanged = false;
    customNodes.forEach(customHeading => {
      if (customHeading.matches('style, link')) {
        if (!customObservedStyles.has(customHeading)) {
          const customIsStyle = customHeading.localName === 'style';
          customAssetObserver.observe(customHeading, {
            attributes: true, attributeOldValue: true,
            attributeFilter: ['href', 'rel', 'media', 'disabled', 'type'],
            childList: customIsStyle, characterData: customIsStyle, subtree: customIsStyle
          });
          customObservedStyles.add(customHeading);
        }
        if (customHeading.matches(customStylesheetSelector)) customStylesChanged = true;
        return;
      }
      if (!customHeadings.has(customHeading)) {
        customHeadings.set(customHeading, { customOriginal: customProperties.map(customProperty => [customHeading.style.getPropertyValue(customProperty), customHeading.style.getPropertyPriority(customProperty)]), customMeasurement: null });
      }
      customInvalidate(customHeading);
    });
    if (customPending.size) customSchedule();
    return customStylesChanged;
  }

  function customContains(customNode, customSelector) {
    return customNode instanceof Element && (customNode.matches(customSelector) || !!customNode.querySelector(customSelector));
  }

  function customFlushRoots() {
    let customStylesChanged = false;
    customAddedRoots.forEach(customRoot => {
      if (!customRoot.isConnected) return;
      for (let customParent = customRoot.parentElement; customParent; customParent = customParent.parentElement) {
        if (customAddedRoots.has(customParent)) return;
      }
      if (customRegister(customRoot)) customStylesChanged = true;
    });
    if (customRemovedRoots.size) {
      // Existing entries identify removed headings without scanning every
      // detached app subtree. Moved headings are measured at their new parent.
      customHeadings.forEach((customEntry, customHeading) => {
        if (!customHeading.isConnected) customPending.add(customHeading);
      });
      customRemovedRoots.forEach(customRoot => {
        if (customContains(customRoot, customStylesheetSelector)) customStylesChanged = true;
      });
    }
    customAddedRoots.clear();
    customRemovedRoots.clear();
    if (customStylesChanged) customScheduleStyles();
  }

  // App text, image URLs and preload links do not change heading typography.
  // Watch text/asset attributes only on real style and link elements.
  const customAssetObserver = new MutationObserver(customMutations => {
    if (customMutations.some(customMutation => {
      const customTarget = customMutation.target.nodeType === Node.ELEMENT_NODE ? customMutation.target : customMutation.target.parentElement;
      return customTarget?.isConnected && (customTarget.matches(customStylesheetSelector) ||
        (customTarget.localName === 'link' && customMutation.attributeName === 'rel' && customMutation.oldValue?.split(/\s+/).includes('stylesheet')));
    })) customScheduleStyles();
  });

  customRegister(document);
  window.addEventListener('resize', customInvalidateAll, { passive: true });
  document.addEventListener('shopify:section:load', customEvent => customRegister(customEvent.target));
  document.addEventListener('shopify:section:unload', customEvent => customRegister(customEvent.target));
  // Lazy section styles can change the baseline after the initial document load.
  document.addEventListener('load', customEvent => {
    if (customEvent.target.matches?.('link[rel~="stylesheet"]')) customScheduleStyles();
  }, true);
  document.fonts?.ready.then(customScheduleStyles);
  document.fonts?.addEventListener('loadingdone', customScheduleStyles);
  new MutationObserver(customMutations => {
    customMutations.forEach(customMutation => {
      const customTarget = customMutation.target;
      if (customMutation.type === 'attributes') {
        if (customMutation.attributeName.startsWith('data-custom-heading-')) {
          if (customHeadings.has(customTarget)) {
            customPending.add(customTarget);
            customSchedule();
          }
          else if (customTarget.matches(customHeadingSelector)) customRegister(customTarget);
        }
        return;
      }
      // Style content has its own observer. Ordinary text replacements need
      // no discovery; only element subtrees can introduce or remove headings.
      if (customTarget.nodeName === 'STYLE') return;
      customMutation.addedNodes.forEach(customNode => {
        if (customNode instanceof Element) customAddedRoots.add(customNode);
      });
      customMutation.removedNodes.forEach(customNode => {
        if (customNode instanceof Element) customRemovedRoots.add(customNode);
      });
    });
    if (customAddedRoots.size || customRemovedRoots.size) customSchedule();
  }).observe(document.documentElement, {
    childList: true, subtree: true, attributes: true,
    attributeFilter: ['data-custom-heading-scale', 'data-custom-heading-align', 'data-custom-heading-align-mobile', 'data-custom-heading-align-tablet']
  });
})();
